import { create } from 'zustand'
import type { Product } from '../types/product'
import type { PaginatedResponse } from '../types/common'
import { ProductsService, type ProductsFilters } from '../services/api/productsService'

interface ProductsState {
  products: PaginatedResponse<Product> | null
  currentProduct: Product | null
  isLoading: boolean
  error: string | null
  filters: ProductsFilters
  
  // Actions
  fetchProducts: () => Promise<void>
  fetchProduct: (id: string) => Promise<void>
  createProduct: (product: Partial<Product>) => Promise<void>
  updateProduct: (id: string, product: Partial<Product>) => Promise<void>
  deleteProduct: (id: string) => Promise<void>
  setFilters: (filters: Partial<ProductsFilters>) => void
  resetFilters: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
}

const defaultFilters: ProductsFilters = {
  query: '',
  toValidate: null,
  page: 1,
  limit: 20,
  sortBy: undefined,
}

export const useProductsStore = create<ProductsState>()((set, get) => ({
  products: null,
  currentProduct: null,
  isLoading: false,
  error: null,
  filters: defaultFilters,

  setLoading: (loading: boolean) => {
    set({ isLoading: loading })
  },

  setError: (error: string | null) => {
    set({ error })
  },

  setFilters: (newFilters: Partial<ProductsFilters>) => {
    const currentFilters = get().filters
    const updatedFilters = { ...currentFilters, ...newFilters }
    set({ filters: updatedFilters })
    // Don't fetch automatically - let the component decide when to fetch
  },

  resetFilters: () => {
    set({ filters: defaultFilters })
    // Don't fetch automatically - let the component decide when to fetch
  },

  fetchProducts: async () => {
    const { filters } = get()
    set({ isLoading: true, error: null })
    
    try {
      const products = await ProductsService.getProducts(filters)
      set({ products, isLoading: false, error: null })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors du chargement des produits'
      set({ isLoading: false, error: errorMessage })
    }
  },

  fetchProduct: async (id: string) => {
    set({ isLoading: true, error: null })
    
    try {
      const product = await ProductsService.getProduct(id)
      set({ currentProduct: product, isLoading: false, error: null })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors du chargement du produit'
      set({ isLoading: false, error: errorMessage })
    }
  },

  createProduct: async (product: Partial<Product>) => {
    set({ isLoading: true, error: null })
    
    try {
      await ProductsService.createProduct(product)
      set({ isLoading: false, error: null })
      // Refresh products list
      get().fetchProducts()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de la création du produit'
      set({ isLoading: false, error: errorMessage })
      throw error
    }
  },

  updateProduct: async (id: string, product: Partial<Product>) => {
    set({ isLoading: true, error: null })
    
    try {
      const updatedProduct = await ProductsService.updateProduct(id, product)
      set({ currentProduct: updatedProduct, isLoading: false, error: null })
      // Refresh products list
      get().fetchProducts()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de la mise à jour du produit'
      set({ isLoading: false, error: errorMessage })
      throw error
    }
  },

  deleteProduct: async (id: string) => {
    set({ isLoading: true, error: null })
    
    try {
      await ProductsService.deleteProduct(id)
      set({ isLoading: false, error: null })
      // Refresh products list
      get().fetchProducts()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de la suppression du produit'
      set({ isLoading: false, error: errorMessage })
      throw error
    }
  },
}))